import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Breadcrumbs } from '~/chords/Breadcrumbs';
import { Button } from '~/components/Button';
import { Icon } from '~/components/Icon';
import { Pagination } from '~/components/Pagination/Pagination';
import { SearchBar } from '~/components/SearchBar/SearchBar';
import { Table } from '~/components/Table';
import { showToast } from '~/components/Toast';

import { useCampaigns } from './api/useCampaigns';
import { useCampaignColumns } from './hooks/useCampaignColumns';

const PER_PAGE = 10;

export function AutomatedCreatorCampaignPage() {
  const { t } = useTranslation('automated-creator-campaign');
  const columns = useCampaignColumns();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const offset = (page - 1) * PER_PAGE;

  const { campaigns, totalCount, isFetching, isError } = useCampaigns({
    limit: PER_PAGE,
    offset,
    ...(searchTerm ? { track_name: searchTerm } : {}),
  });

  useEffect(() => {
    if (isError) {
      showToast({
        type: 'error',
        title: t('errors.fetchCampaignsTitle'),
        description: t('errors.fetchCampaignsDescription'),
      });
    }
  }, [isError, t]);

  const tableData = useMemo(
    () =>
      (campaigns ?? []).map((campaign) => ({
        ...campaign,
        id: String(campaign.id),
      })),
    [campaigns],
  );

  const total = totalCount ?? 0;

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleClear = () => {
    setSearchTerm('');
    setPage(1);
  };

  return (
    <div className="p-xl">
      <Breadcrumbs.Root>
        <Breadcrumbs.Item to="/marketing">
          {t('breadcrumb.marketing')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item>{t('breadcrumb.current')}</Breadcrumbs.Item>
      </Breadcrumbs.Root>

      <div className="mt-md flex items-center justify-between">
        <h1 className="text-display-sm font-bold">{t('title')}</h1>
        <Button asChild variant="primary" size="large">
          <Link to="/marketing/automated-creator-campaigns/new">
            <Icon type="plus" size="sm" className="mr-xs" />
            {t('startNewCampaign')}
          </Link>
        </Button>
      </div>

      <div className="mt-xl flex items-end justify-between">
        <div>
          <h2 className="text-headline-sm font-semibold">
            {t('campaigns.heading', { count: total })}
          </h2>
          <p className="text-body-sm">{t('campaigns.subtitle')}</p>
        </div>
        <Pagination
          page={page}
          perPage={PER_PAGE}
          total={total}
          setPageNumber={setPage}
        />
      </div>

      <div className="mt-md">
        <SearchBar
          placeholder={t('campaigns.searchPlaceholder')}
          onSubmit={handleSearch}
          onClear={handleClear}
          formAriaLabel={t('campaigns.searchPlaceholder')}
          inputAriaLabel={t('campaigns.searchPlaceholder')}
        />
      </div>

      <div className="mt-md">
        <Table
          columns={columns}
          data={tableData}
          isLoading={isFetching}
          loadingRowCount={PER_PAGE}
          size="md"
          variant="striped"
        />
      </div>
    </div>
  );
}

AutomatedCreatorCampaignPage.displayName = 'AutomatedCreatorCampaignPage';
